import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import invariant from 'tiny-invariant'
import { socketOn, socketOff } from 'src/lib/socket'

type OnlineUsers = Record<string, boolean>

interface OnlineUserContextValue {
  onlineUsers: OnlineUsers
  setOnlineUsers: (userId: string, isOnline: boolean) => void
}

const OnlineUserContext = createContext<OnlineUserContextValue>(
  {} as OnlineUserContextValue
)

export const useOnlineUserContext = () => {
  const context = useContext(OnlineUserContext)

  invariant(
    context,
    'useOnlineUserContext must be used within an OnlineUserContextProvider'
  )

  return context
}

export const OnlineUserContextProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [onlineUsers, setOnlineUsers] = useState<OnlineUsers>({})
  const listenersRegistered = useRef(false)

  const setUserOnlineStatus = useCallback(
    (userId: string, isOnline: boolean) => {
      setOnlineUsers(prev => ({ ...prev, [userId]: isOnline }))
    },
    []
  )

  // Register global socket listeners once
  useEffect(() => {
    if (listenersRegistered.current) return
    listenersRegistered.current = true

    const handleUserOnline = (data: { userId: string }) => {
      setOnlineUsers(prev => ({ ...prev, [data.userId]: true }))
    }

    const handleUserOffline = (data: { userId: string }) => {
      setOnlineUsers(prev => ({ ...prev, [data.userId]: false }))
    }

    socketOn('userOnline', handleUserOnline)
    socketOn('userOffline', handleUserOffline)

    return () => {
      socketOff('userOnline', handleUserOnline)
      socketOff('userOffline', handleUserOffline)
      listenersRegistered.current = false
    }
  }, [])

  return (
    <OnlineUserContext.Provider
      value={{
        onlineUsers,
        setOnlineUsers: setUserOnlineStatus
      }}>
      {children}
    </OnlineUserContext.Provider>
  )
}
